import {DatePicker, Form, Input, Spin, Table, Tag, Tooltip} from 'antd';
import moment from 'moment';
import {useState} from 'react';
import {currencySymbols} from 'utils/mock';
import styles from './customerPerformance.module.scss';
import {fieldRules, renderTransactionId} from 'utils/helper';
import {SelectOutlined} from '@ant-design/icons';
import WireTransferDetails from 'pages/wallet/transactions/transferDetails/WireTransferDetails';
import DBModal from 'components/modal/modal';
// import PercentageCard from 'pages/adminUser/card/PercentageCard';
import {useSelector} from 'react-redux';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {Button} from '@dabafinance/react-components';
import {CustomerPerformanceHook} from './hooks';
import {FinancialCurrencyCard} from 'pages/financials/FinancialCards';
import {DABA_PRO_PLAN} from 'pages/adminUser/userDetails/tabs/Interactions/UserInteractions';
import {UserSelectField} from 'components/userSelectField/UserSelectField';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import UserLink from 'components/userLink/UserLink';

const formatRegionEnumCell = value =>
  value
    ? String(value)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, c => c.toUpperCase())
    : '—';

export const CustomerPerformance = () => {
  const [copied, setCopied] = useState(false);
  const [openBankModal, setOpenBankModal] = useState(false);
  const [currentViewType, setCurrentViewType] = useState('STATS');
  const [bankDetails, setBankDetails] = useState({});
  const adminRole = useSelector(
    state => state?.auth?.userData?.user?.activeAdminTab?.name,
  );
  const {
    data,
    handleDateFilter,
    form,
    loading,
    handleExport,
    totalPages,
    currentPage,
    handlePageChange,
    exportForm,
    setOpenExportModal,
    limit,
    openExportModal,
    activeTab,
    stats,
    statsLoading,
    setActiveTab,
    agent,
    setAgent,
  } = CustomerPerformanceHook({adminRole});

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.AgentPerformance),
  );

  const handleCopy = referenceId => {
    navigator.clipboard.writeText(referenceId).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 400);
    });
  };

  const handleOpenTxModal = (data, event) => {
    if (event.target.isConnected) {
      setOpenBankModal(true);
      setBankDetails(data);
    }
  };

  const column = [
    {
      title: 'Reference ID',
      dataIndex: 'referenceId',
      key: 'referenceId',
      render: (text, record) => (
        <Tooltip
          trigger="hover"
          title={copied ? 'Copied' : record?.referenceId}>
          <p
            onClick={e => {
              e.stopPropagation();
              handleCopy(record?.referenceId);
            }}
            className={styles.reference}>
            {renderTransactionId(record?.referenceId)}
          </p>
        </Tooltip>
      ),
    },
    {
      title: 'Provider ID',
      dataIndex: 'providerId',
      key: 'providerId',
      render: (text, record) => (
        <Tooltip
          trigger="hover"
          title={
            copied ? 'Copied' : record?.paymentGateway?.metadata?.transactionId
          }>
          <p
            onClick={e => {
              e.stopPropagation();
              handleCopy(record?.paymentGateway?.metadata?.transactionId);
            }}
            className={styles.reference}>
            {renderTransactionId(
              record?.paymentGateway?.metadata?.transactionId,
            )}
          </p>
        </Tooltip>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <UserLink
          userId={record?.user?.id}
          tab="wallet-transactions"
          className={styles.clickableUserName}>
          {`${record?.user?.firstName ?? ''} ${
            record?.user?.lastName ?? ''
          }`.trim() || '—'}
        </UserLink>
      ),
    },
    {
      title: 'Region',
      key: 'region',
      render: (_, record) => (
        <span>
          {formatRegionEnumCell(record?.user?.regionCategory?.region)}
        </span>
      ),
    },
    {
      title: 'Sub-region',
      key: 'subRegion',
      render: (_, record) => (
        <span>
          {formatRegionEnumCell(record?.user?.regionCategory?.subRegion)}
        </span>
      ),
    },
    {
      title: 'Transaction Type',
      dataIndex: 'type',
      key: 'type',
      render: (text, record) => (
        <p>
          {record?.type
            ?.replace(/_/g, ' ')
            ?.toLowerCase()
            ?.replace(/\b\w/g, c => c.toUpperCase())}
        </p>
      ),
    },

    {
      title: 'Direction',
      dataIndex: 'direction',
      key: 'direction',
      render: (text, record) => (
        <Tag
          color={
            {
              'DEPOSIT': 'black',
              'WITHDRAWAL': 'default',
              'STOCK_BUY': 'magenta',
              'STOCK_SELL': 'cyan',
              'DIVIDEND_PAYOUT': 'orange',
              'DIVIDEND_REVERSAL': 'red',
              'CURRENCY_SWAP': 'geekblue',
              'VENTURE_INVESTMENT': 'purple',
              'VENTURE_FUND_INVESTMENT': 'gold',
              'BOND_INVESTMENT': 'volcano',
              'REWARDS_PAYIN': 'lime',
              'REWARDS_PAYOUT': 'lime',
              'DABA_PRO': 'blue',
            }[record?.direction] || 'blue'
          }>
          {record?.direction?.replace(/_/g, ' ')}
        </Tag>
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'paymentProvider',
      key: 'paymentProvider',
      render: (text, record) => (
        <span>
          {record?.paymentProvider
            ?.toLowerCase()
            ?.replace(/\b\w/g, c => c.toUpperCase())}
        </span>
      ),
    },
    {
      title: 'Payment Method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (text, record) => (
        <div style={{width: 170}}>
          <span>
            {record?.paymentMetadata?.paymentMethod ? (
              <Tag
                color={
                  record?.paymentMetadata?.paymentMethod === 'MOBILE_MONEY'
                    ? 'cyan'
                    : 'geekblue'
                }>
                {record?.paymentMetadata?.paymentMethod
                  .replace(/_/g, ' ')
                  .toLowerCase()
                  .replace(/\b\w/g, c => c.toUpperCase())}
              </Tag>
            ) : (
              '-- -- --'
            )}
          </span>
          <SelectOutlined
            onClick={e => {
              e.stopPropagation();
              handleOpenTxModal(
                {
                  ...record?.paymentMetadata,
                  email: record?.paymentGateway?.metadata?.email,
                  paymentProvider: record?.paymentProvider,
                },
                e,
              );
            }}
          />
        </div>
      ),
    },
    {
      title: 'Sales Agent',
      dataIndex: 'claimedBy',
      key: 'claimedBy',
      render: (text, record) => (
        <p>{record?.claimedTransaction?.createdBy?.email ?? '- -'}</p>
      ),
    },
    {
      title: 'Total Amount',
      dataIndex: 'amountNetOfFees',
      key: 'amountNetOfFees',
      render: (text, record) => (
        <span>
          {currencySymbols[record?.currency]?.symbol}
          {Number(
            record?.totalTransactionAmountIncludingFees || 0,
          )?.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Amount (Net of Fees)',
      dataIndex: 'amountNetOfFees',
      key: 'amountNetOfFees',
      render: (text, record) => (
        <span>
          {currencySymbols[record?.currency]?.symbol}
          {Number(record?.transactionAmountNetOfFees || 0)?.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Fees',
      dataIndex: 'fees',
      key: 'fees',
      render: (text, record) => (
        <span>
          {currencySymbols[record?.currency]?.symbol}
          {Number(record?.transactionFee || 0)?.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, render) => (
        <Tag
          color={
            {
              'SUCCESSFUL': 'green',
              'PENDING': 'yellow',
              'FAILED': 'red',
              'CANCELLED': 'red',
            }[render?.status] || 'blue'
          }
          key={render.enabled}>
          {render.status}
        </Tag>
      ),
    },
    {
      title: 'Date & Time',
      dataIndex: 'time',
      key: 'time',
      render: (text, record) => (
        <p className={styles.reference}>
          {moment(record?.createdAt).format('M/D/YY hh:mm a')}
        </p>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Sales Agent Performance</h1>
      </div>
      <div className={styles.content}>
        <div className={styles.top}>
          <Form
            form={form}
            className={styles.form}
            onFinish={handleDateFilter}
            layout="horizontal">
            {adminRole === 'SUPERADMIN' && (
              <Form.Item name="agent" className={styles.search}>
                <UserSelectField
                  users={agent}
                  setUsers={setAgent}
                  placeholder="Search and Select Agent"
                  mode="single"
                  admin
                />
              </Form.Item>
            )}
            <Form.Item name="startDate">
              <DatePicker placeholder="Start Date" showTime />
            </Form.Item>
            <Form.Item name="endDate">
              <DatePicker placeholder="End Date" showTime />
            </Form.Item>
            <SubmitButton disabled={loading} label="Filter" smallWidth />
          </Form>
          <Button
            label="Export"
            disabled={!tabOperations?.export}
            onClick={() => setOpenExportModal({open: true, email: null})}
          />
        </div>
        <div className={styles['details-toggler']}>
          {adminRole !== 'SUPERADMIN' && (
            <span
              className={activeTab === 'ADMIN' ? styles.active : ''}
              onClick={() => {
                setActiveTab('ADMIN');
                form.resetFields();
                exportForm.resetFields();
              }}>
              My Performance
            </span>
          )}
          <span
            className={activeTab === 'TEAM' ? styles.active : ''}
            onClick={() => {
              setActiveTab('TEAM');
              form.resetFields();
              exportForm.resetFields();
            }}>
            Team Performance
          </span>
        </div>
        <div className={styles['details-toggler']}>
          <span
            className={currentViewType === 'STATS' ? styles.active : ''}
            onClick={() => {
              setCurrentViewType('STATS');
            }}>
            Statistics
          </span>
          <span
            className={currentViewType === 'HISTORY' ? styles.active : ''}
            onClick={() => {
              setCurrentViewType('HISTORY');
            }}>
            History
          </span>
        </div>
        {statsLoading && currentViewType === 'STATS' && (
          <div className={styles.noRecord}>
            <Spin />
            <h2>Loading...</h2>
          </div>
        )}
        {currentViewType === 'STATS' ? (
          <>
            {!stats?.totalAmountDeposited?.length &&
              !stats?.totalTransactionVolume?.length &&
              !stats?.totalAmountInvested?.length &&
              !stats?.totalDabaProSubscription?.length &&
              !stats?.totalDabaAcademySubscription?.length &&
              !stats?.totalSimulatorTransactions?.length &&
              !statsLoading && (
                <div className={styles.noRecord}>
                  <h2>No Record found</h2>
                </div>
              )}
            <div className={styles.cards}>
              {stats?.['totalTransactionVolume']?.map(item => (
                <FinancialCurrencyCard
                  key={item?.currency}
                  loading={statsLoading}
                  type={'PLN'}
                  currency={`Total BUY + SELL Transaction Volume (${item?.currency})`}
                  amount={item.amount}
                />
              ))}
            </div>
            <div className={styles.cards}>
              {stats?.['totalAmountDeposited']?.map(item => (
                <FinancialCurrencyCard
                  key={item?.currency}
                  loading={statsLoading}
                  type={'PLN'}
                  currency={`Total Amount Deposited (${item?.currency})`}
                  amount={item.amount}
                />
              ))}
            </div>
            <div className={styles.cards}>
              {stats?.['totalAmountInvested']?.map(item => (
                <FinancialCurrencyCard
                  key={item?.currency}
                  loading={statsLoading}
                  type={'PLN'}
                  currency={`Initiated Transaction Volume (${item?.currency})`}
                  amount={item.amount}
                />
              ))}
            </div>
            <div className={styles.cards}>
              {stats?.['totalSettledTransactionVolume']?.map(item => (
                <FinancialCurrencyCard
                  key={item?.currency}
                  loading={statsLoading}
                  type={'PLN'}
                  currency={`Settled Transaction Volume (${item?.currency})`}
                  amount={item.amount}
                />
              ))}
            </div>
            {stats?.['settledTransactionByAssetClass']?.length > 0 && (
              <div className={styles.cards}>
                {(() => {
                  // Group by assetClass
                  const groupedByAssetClass = {};
                  stats?.['settledTransactionByAssetClass']?.forEach(item => {
                    const assetClass = item?.assetClass || 'UNKNOWN';
                    if (!groupedByAssetClass[assetClass]) {
                      groupedByAssetClass[assetClass] = [];
                    }
                    groupedByAssetClass[assetClass].push(item);
                  });

                  return Object.keys(groupedByAssetClass).map(assetClass => (
                    <div key={assetClass} className={styles.breakdownCard}>
                      <h3 className={styles.breakdownTitle}>
                        {assetClass.replace(/_/g, ' ')}
                      </h3>
                      {groupedByAssetClass[assetClass].map((item, index) => (
                        <div key={`${assetClass}-${item?.currency}-${index}`}>
                          <div className={styles.breakdownItem}>
                            <span className={styles.breakdownLabel}>
                              Currency ({item?.currency})
                            </span>
                            <span className={styles.breakdownValue}>
                              {item?.currency}
                            </span>
                          </div>
                          <div className={styles.breakdownItem}>
                            <span className={styles.breakdownLabel}>
                              Volume
                            </span>
                            <span className={styles.breakdownValue}>
                              {statsLoading
                                ? '...'
                                : Number(item?.volume || 0).toLocaleString()}
                            </span>
                          </div>
                          <div className={styles.breakdownItem}>
                            <span className={styles.breakdownLabel}>Count</span>
                            <span className={styles.breakdownValue}>
                              {statsLoading
                                ? '...'
                                : Number(item?.count || 0).toLocaleString()}
                            </span>
                          </div>
                          <div className={styles.breakdownItem}>
                            <span className={styles.breakdownLabel}>
                              Total Fee
                            </span>
                            <span className={styles.breakdownValue}>
                              {statsLoading
                                ? '...'
                                : isNumber(item?.fees?.totalFee)}
                            </span>
                          </div>
                          <div className={styles.breakdownItem}>
                            <span className={styles.breakdownLabel}>
                              Variable Fee
                            </span>
                            <span className={styles.breakdownValue}>
                              {statsLoading
                                ? '...'
                                : isNumber(item?.fees?.variableFee)}
                            </span>
                          </div>
                          <div className={styles.breakdownItem}>
                            <span className={styles.breakdownLabel}>
                              Static Fee
                            </span>
                            <span className={styles.breakdownValue}>
                              {statsLoading
                                ? '...'
                                : isNumber(item?.fees?.staticFee)}
                            </span>
                          </div>
                          <div className={styles.breakdownItem}>
                            <span className={styles.breakdownLabel}>
                              Fixed Fee
                            </span>
                            <span className={styles.breakdownValue}>
                              {statsLoading
                                ? '...'
                                : isNumber(item?.fees?.fixedFee)}
                            </span>
                          </div>
                          {index <
                            groupedByAssetClass[assetClass].length - 1 && (
                            <div className={styles.breakdownSeparator} />
                          )}
                        </div>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            )}
            <div className={styles.cards}>
              {stats?.['totalDabaProSubscription']?.map(item => (
                <FinancialCurrencyCard
                  key={item?.type}
                  loading={statsLoading}
                  type={'PLN'}
                  currency={`Total Daba Pro Subscription (${
                    DABA_PRO_PLAN.find(plan => plan.value === item?.type)
                      ?.label || '1 Year'
                  })`}
                  amount={item.total}
                />
              ))}
            </div>
            <div className={styles.cards}>
              <div className={styles.rowCards}>
                {stats?.['totalDabaProSubscription']?.[0]?.breakDown?.map(
                  item => (
                    <FinancialCurrencyCard
                      key={item?.currency}
                      loading={statsLoading}
                      type={'PLN'}
                      currency={item?.currency}
                      amount={item.total}
                    />
                  ),
                )}
              </div>
              <div className={styles.rowCards}>
                {stats?.['totalDabaProSubscription']?.[1]?.breakDown?.map(
                  item => (
                    <FinancialCurrencyCard
                      key={item?.currency}
                      loading={statsLoading}
                      type={'PLN'}
                      currency={item?.currency}
                      amount={item.total}
                    />
                  ),
                )}
              </div>
              <div className={styles.rowCards}>
                {stats?.['totalDabaProSubscription']?.[2]?.breakDown?.map(
                  item => (
                    <FinancialCurrencyCard
                      key={item?.currency}
                      loading={statsLoading}
                      type={'PLN'}
                      currency={item?.currency}
                      amount={item.total}
                    />
                  ),
                )}
              </div>
            </div>
            <div className={styles.cards}>
              {stats?.['totalDabaAcademySubscription']?.map(item => (
                <FinancialCurrencyCard
                  key={item?.currency}
                  loading={statsLoading}
                  type={'PLN'}
                  currency={`Total Daba Academy Subscription (${item?.currency})`}
                  amount={item?.count}
                />
              ))}
            </div>
            {stats?.totalSimulatorTransactions?.length > 0 && (
              <div className={styles.cards}>
                {stats.totalSimulatorTransactions.map((simItem, simIndex) => {
                  const typeLabel = (simItem?.type || 'UNKNOWN')
                    .replace(/_/g, ' ')
                    .toLowerCase()
                    .replace(/\b\w/g, c => c.toUpperCase());
                  const orderedBreakDown = [...(simItem?.breakDown ?? [])].sort(
                    (a, b) =>
                      (a?.currency || '').localeCompare(b?.currency || ''),
                  );
                  return (
                    <div
                      key={`${simItem?.type}-${simIndex}`}
                      className={styles.breakdownCard}>
                      <h3 className={styles.breakdownTitle}>
                        Simulator — {typeLabel}
                      </h3>
                      <div className={styles.breakdownItem}>
                        <span className={styles.breakdownLabel}>Total</span>
                        <span className={styles.breakdownValue}>
                          {statsLoading
                            ? '...'
                            : Number(simItem?.total ?? 0).toLocaleString()}
                        </span>
                      </div>
                      {orderedBreakDown.map((row, rowIndex) => {
                        const rowSymbol =
                          currencySymbols[row?.currency]?.symbol ?? '';
                        return (
                          <div key={`${row?.currency}-${rowIndex}`}>
                            {rowIndex > 0 && (
                              <div className={styles.breakdownSeparator} />
                            )}
                            <div className={styles.breakdownItem}>
                              <span className={styles.breakdownLabel}>
                                Currency
                              </span>
                              <span className={styles.breakdownValue}>
                                {row?.currency ?? '—'}
                              </span>
                            </div>
                            <div className={styles.breakdownItem}>
                              <span className={styles.breakdownLabel}>
                                Amount
                              </span>
                              <span className={styles.breakdownValue}>
                                {statsLoading
                                  ? '...'
                                  : `${rowSymbol}${Number(
                                      row?.amount ?? 0,
                                    ).toLocaleString()}`}
                              </span>
                            </div>
                            <div className={styles.breakdownItem}>
                              <span className={styles.breakdownLabel}>
                                Count
                              </span>
                              <span className={styles.breakdownValue}>
                                {statsLoading
                                  ? '...'
                                  : Number(row?.count ?? 0).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <div>
            <Table
              loading={loading}
              dataSource={data}
              columns={column}
              className={styles.table}
              pagination={{
                current: currentPage,
                total: totalPages,
                onChange: handlePageChange,
                showSizeChanger: true,
                pageSize: limit,
              }}
            />
          </div>
        )}
      </div>
      <DBModal
        isOpen={openExportModal.open}
        handleClose={() => setOpenExportModal({open: false, email: null})}
        content={
          <div className={styles.exportModal}>
            <div className={styles.exportHeader}>
              <h2>Export History Data</h2>
              <p>Note that the export is based on filters if applied.</p>
            </div>
            <Form form={exportForm} layout="vertical">
              <Form.Item
                rules={[
                  fieldRules('Email'),
                  {type: 'email', message: 'Invalid email address'},
                ]}
                label="Email"
                name="email">
                <Input placeholder="Email" />
              </Form.Item>
              <div className="buttons">
                <Button
                  label="Cancel"
                  onClick={() => setOpenExportModal({open: false, email: null})}
                />
                <Button
                  type="secondary"
                  label="Export"
                  onClick={handleExport}
                  disabled={loading}
                />
              </div>
            </Form>
          </div>
        }
      />
      <DBModal
        isOpen={openBankModal}
        content={<WireTransferDetails data={bankDetails} />}
        handleClose={() => setOpenBankModal(false)}
      />
    </div>
  );
};

const isNumber = value => {
  return Number.isNaN(Number(value)) ? '***' : Number(value)?.toLocaleString();
};
